import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Container, Header, Segment, Image, Card, Button, Label, Loader, Dimmer, Message, Grid,
} from 'semantic-ui-react';
import moment from 'moment';
import { getOrganiser } from './lib/tickets';
import { buildPath } from './lib/tenant';
import { t } from './locales';
import { formatMoney } from './lib/currency';

function Organiser() {
    const navigate = useNavigate();

    const { data, isLoading, isError } = useQuery({
        queryKey: ['ticketing-organiser'],
        queryFn: getOrganiser,
    });

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader content={t('common.loadingOrganiser')} />
            </Dimmer>
        );
    }

    if (isError || !data) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('organiser.loadErrorTitle')}</Message.Header>
                </Message>
            </Container>
        );
    }

    return (
        <Container className="contentContainer">
            {/* Organiser hero */}
            <Segment style={{ borderRadius: 12, marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {data.logo && (
                        <Image
                            src={data.logo}
                            size="small"
                            rounded
                            style={{ maxHeight: 120, objectFit: 'contain' }}
                        />
                    )}
                    <div>
                        <Header as="h1" style={{ color: 'var(--colour_menu_main)', marginBottom: '0.25rem' }}>
                            {data.name}
                        </Header>
                        {data.description && (
                            <p style={{ color: '#555', maxWidth: 600 }}>{data.description}</p>
                        )}
                        {data.contact_email && (
                            <a href={`mailto:${data.contact_email}`} style={{ color: 'var(--colour_menu_main)' }}>
                                {data.contact_email}
                            </a>
                        )}
                    </div>
                </div>
            </Segment>

            <Header as="h2" style={{ color: 'var(--colour_menu_main)' }}>
                {t('organiser.eventsByOrganiser', { organiserName: data.name })}
            </Header>

            {data.events.length === 0 && (
                <Message info>
                    <Message.Header>{t('common.noEventsAvailable')}</Message.Header>
                    <p>{t('organiser.noEventsBody')}</p>
                </Message>
            )}

            <Grid stackable columns={3}>
                {data.events.map(event => (
                    <Grid.Column key={event.id}>
                        <Card
                            fluid
                            onClick={() => navigate(buildPath(`/events/${event.id}`))}
                            style={{ borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}
                        >
                            {event.image_url ? (
                                <Image
                                    src={event.image_url}
                                    wrapped
                                    ui={false}
                                    style={{ height: 160, objectFit: 'cover', width: '100%' }}
                                />
                            ) : (
                                <div style={{
                                    height: 160,
                                    background: 'linear-gradient(135deg, var(--colour_menu_main), var(--colour_menu_light))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '3rem' }}>🎟</span>
                                </div>
                            )}
                            <Card.Content>
                                <Card.Header style={{ color: 'var(--colour_menu_main)' }}>
                                    {event.name}
                                </Card.Header>
                                {event.next_instance?.date_time && (
                                    <Card.Meta>
                                        {moment(event.next_instance.date_time).format('ddd D MMM YYYY, HH:mm')}
                                    </Card.Meta>
                                )}
                                {event.category && (
                                    <div style={{ marginTop: '0.4rem' }}>
                                        <Label size="tiny" style={{ backgroundColor: '#eee', color: '#555' }}>
                                            {event.category}
                                        </Label>
                                    </div>
                                )}
                            </Card.Content>
                            <Card.Content extra style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Label style={{ backgroundColor: 'var(--colour_heading_success)', color: '#fff', fontWeight: 700 }}>
                                    {formatMoney(event.ticket_price)}
                                </Label>
                                <Button size="small" style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff' }}>
                                    {t('common.getTickets')}
                                </Button>
                            </Card.Content>
                        </Card>
                    </Grid.Column>
                ))}
            </Grid>
        </Container>
    );
}

export default Organiser;
